import { useEffect, useState } from "react";

import { nav, site } from "../../docs.config.ts";
import { normalizeSpec } from "./parse.ts";
import { type NormalizedSpec, type OpenApiDocument } from "./types.ts";

// O spec é buscado em runtime, não importado no bundle. Trocar
// public/openapi.json publica uma referência nova sem rebuild — que é o ponto
// de ter a API Reference vindo do OpenAPI em vez de escrita à mão.

const apiSection = nav.find((n) => n.type === "openapi");

let cache: NormalizedSpec | null = null;
let inflight: Promise<NormalizedSpec> | null = null;

export function loadSpec(): Promise<NormalizedSpec> {
  if (cache) return Promise.resolve(cache);
  if (!inflight) {
    inflight = fetch(site.openapiUrl)
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status} ao buscar ${site.openapiUrl}`);
        return res.json();
      })
      .then((doc: OpenApiDocument) => {
        cache = normalizeSpec(doc, { tagOrder: apiSection?.tagOrder || [] });
        return cache;
      })
      .catch((err) => {
        // Sem isto, um erro de rede transitório deixaria a promise rejeitada em
        // cache e a referência nunca mais carregaria nesta sessão.
        inflight = null;
        throw err;
      });
  }
  return inflight;
}

interface SpecState {
  spec: NormalizedSpec | null;
  loading: boolean;
  error: Error | null;
}

function useOpenApi(): SpecState {
  const [state, setState] = useState<SpecState>(() =>
    cache ? { spec: cache, loading: false, error: null } : { spec: null, loading: true, error: null }
  );

  useEffect(() => {
    if (cache) return undefined;
    let alive = true;
    loadSpec()
      .then((spec) => alive && setState({ spec, loading: false, error: null }))
      .catch((error: Error) => alive && setState({ spec: null, loading: false, error }));
    return () => {
      alive = false;
    };
  }, []);

  return state;
}

export default useOpenApi;
